/**
 * AppHeader - Sticky header.
 *
 * Mobile (<md): avatar (opens MobileDrawer) | logo | messages icon (opens MessagesOverlay).
 * Desktop (md+): logo + app name (left), SearchBar (center), profile link (right: username | avatar).
 */

import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectUser } from '../../store/auth.reducer';
import SearchBar from '../Search/SearchBar';
import NavIcon from './NavIcon';
import UserAvatar from '../Profile/UserAvatar';
import { useMessageCount } from '../../utils/MessageCountContext';

function AppHeader({ onOpenDrawer, onOpenMessages }) {
  const user = useSelector(selectUser);
  const [, setSearchParams] = useSearchParams();
  const { unreadMessageCount } = useMessageCount();

  const handleHashtagSelect = (tag) => {
    setSearchParams(tag ? { hashtag: tag } : {});
  };

  const displayName = user?.displayname || user?.username || 'Profile';

  return (
    <header className="sticky top-0 z-10 flex-shrink-0 bg-void/95 backdrop-blur border-b border-shadow pt-safe-top">
      <div className="md:hidden w-full px-4 py-3 flex items-center justify-between gap-4">
        <button
          type="button"
          onClick={onOpenDrawer}
          aria-label={`Open menu for ${displayName}`}
          data-testid="header-avatar-button"
          className="flex-shrink-0 rounded-full hover:opacity-90"
        >
          <UserAvatar user={user} className="w-9 h-9 rounded-full" emojiClassName="text-base" />
        </button>
        <Link to="/" className="flex-shrink-0 hover:opacity-90" aria-label="Home">
          <img src="/aurasphere-logo.svg" alt="aurasphere" className="h-9 w-auto" />
        </Link>
        <button
          type="button"
          onClick={onOpenMessages}
          aria-label={
            unreadMessageCount > 0
              ? `Messages, ${unreadMessageCount} unread conversations`
              : 'Messages'
          }
          data-testid="header-messages-button"
          className="relative flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-ash hover:text-ether"
        >
          <NavIcon icon="messages" className="w-5 h-5" />
          {unreadMessageCount > 0 && (
            <span
              className="absolute top-1 right-1 min-w-[1rem] h-4 px-0.5 rounded-full bg-ether text-void text-[9px] font-bold flex items-center justify-center leading-none"
              aria-hidden
            >
              {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
            </span>
          )}
        </button>
      </div>

      <div className="hidden md:grid w-full px-4 py-3 grid-cols-[1fr_3fr_1fr] items-center gap-4">
        <div className="flex justify-center">
          <Link to="/" className="flex items-center gap-4 flex-shrink-0 hover:opacity-90">
            <img src="/aurasphere-logo.svg" alt="" className="h-12 w-auto" />
            <div className="w-px h-6 bg-silver/50" aria-hidden="true" />
            <span className="font-display text-xl font-bold text-ether">aurasphere</span>
          </Link>
        </div>
        <div className="flex justify-center">
          <div className="w-full max-w-xl">
            <SearchBar onHashtagSelect={handleHashtagSelect} />
          </div>
        </div>
        <nav className="flex items-center justify-center gap-2 flex-shrink-0">
          <Link
            to="/profile"
            className="flex items-center gap-2 text-silver hover:text-ether"
            aria-label={`Profile for ${displayName}`}
          >
            <span className="truncate max-w-[120px] font-display text-xl font-bold">{displayName}</span>
            <div className="w-px h-6 bg-silver/50" aria-hidden="true" />
            <UserAvatar user={user} className="w-12 h-12 rounded-full flex-shrink-0" emojiClassName="text-2xl" />
          </Link>
        </nav>
      </div>
    </header>
  );
}

export default AppHeader;
